/**
 * Shared zip-building core used by both the zip worker and the main-thread
 * fallback. Streams entries through fflate's synchronous Zip pipeline.
 *
 * Why fflate and not JSZip here: JSZip's `generateAsync` yields a macrotask
 * per internal ~16KB chunk. Run on a page with an active render loop, every
 * yield hands the main thread to the frame loop, stretching a sub-second zip
 * of a 40MB scene into tens of seconds (measured 53–62s on the F40 scene vs
 * 0.75s with the render loop stopped). fflate builds the archive in one
 * synchronous pass — which is why callers run it inside a worker.
 * JSZip remains in use for *reading* archives.
 */

import { Zip, ZipDeflate, ZipPassThrough } from 'fflate';

/** DEFLATE level: 0 = STORE (no compression), 1–9 = compression effort. */
export type ZipLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface ZipEntry {
    /** Path inside the archive, e.g. `assets/model.glb`. */
    path: string;
    data: Uint8Array;
    level: ZipLevel;
}

export interface ZipProgress {
    /** 0..1 fraction of input bytes consumed into the archive. */
    fraction: number;
    /** Path of the entry just completed. */
    path: string;
    entriesDone: number;
    entriesTotal: number;
}

export type ZipProgressCallback = (progress: ZipProgress) => void;

/**
 * Build a zip archive synchronously. `onEntryDone` fires after each entry is
 * written — inside a worker that maps to progress messages; on the main
 * thread it's a direct callback.
 */
export function buildZipSync(
    entries: ZipEntry[],
    onEntryDone?: ZipProgressCallback,
): Uint8Array {
    const chunks: Uint8Array[] = [];
    let totalOut = 0;
    let zipError: Error | null = null;

    const zip = new Zip((err, chunk, _final) => {
        if (err) {
            zipError = err;
            return;
        }
        chunks.push(chunk);
        totalOut += chunk.length;
    });

    const bytesTotal = entries.reduce((sum, e) => sum + e.data.length, 0);
    let bytesDone = 0;

    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const stream = entry.level > 0
            ? new ZipDeflate(entry.path, { level: entry.level })
            : new ZipPassThrough(entry.path);
        zip.add(stream);
        stream.push(entry.data, true);
        if (zipError) throw zipError;

        bytesDone += entry.data.length;
        onEntryDone?.({
            fraction: bytesTotal > 0 ? bytesDone / bytesTotal : 1,
            path: entry.path,
            entriesDone: i + 1,
            entriesTotal: entries.length,
        });
    }

    zip.end();
    if (zipError) throw zipError;

    const out = new Uint8Array(totalOut);
    let offset = 0;
    for (const chunk of chunks) {
        out.set(chunk, offset);
        offset += chunk.length;
    }
    return out;
}
